import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserAnalyticsThunk,
  clearUserAnalytics,
} from "../../../store/thunks/userThunks";
import Modal from "../../../components/common/Modal";
import Badge from "../../../components/common/Badge";
import {
  ChartBarIcon,
  TrendingUpIcon,
  CalendarIcon,
  ShoppingBagIcon,
  StarIcon,
  LocationMarkerIcon,
  ExclamationIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
} from "@heroicons/react/outline";

const UserAnalyticsDashboard = ({ isOpen, onClose, userId, userName }) => {
  const dispatch = useDispatch();
  const { analytics, loadingAnalytics, analyticsError, loyaltyInfo } =
    useSelector((state) => state.user);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (isOpen && userId) {
      dispatch(getUserAnalyticsThunk(userId));
    }

    return () => {
      if (!isOpen) {
        dispatch(clearUserAnalytics());
      }
    };
  }, [isOpen, userId, dispatch]);

  const dashboard = analytics.userAnalyticsDashboard;

  if (loadingAnalytics) {
    return (
      <Modal
        isOpen={isOpen}
        closeModal={onClose}
        title={`Analytics - ${userName}`}
        isFullScreen={true}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading analytics...</p>
          </div>
        </div>
      </Modal>
    );
  }

  if (analyticsError) {
    return (
      <Modal
        isOpen={isOpen}
        closeModal={onClose}
        title={`Analytics - ${userName}`}
      >
        <div className="text-center py-8">
          <ExclamationIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{analyticsError}</p>
        </div>
      </Modal>
    );
  }

  if (!dashboard) {
    return null;
  }

  const {
    statistics,
    insights,
    behavior,
    visitHistory,
    purchaseHistory,
    dashboardLoyalty,
  } = dashboard;

  const loyalty = loyaltyInfo || dashboardLoyalty;

  // Tab configuration
  const tabs = [
    { id: "overview", label: "Overview", icon: ChartBarIcon },
    { id: "appointments", label: "Appointments", icon: CalendarIcon },
    { id: "purchases", label: "Purchases", icon: ShoppingBagIcon },
    { id: "behavior", label: "Behavior", icon: TrendingUpIcon },
    { id: "loyalty", label: "Loyalty", icon: StarIcon },
  ];

  // Render Overview Tab
  const renderOverview = () => (
    <div className="space-y-6">
      {/* User Info Badge */}
      {statistics?.user && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
              {statistics.user.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="font-semibold">{statistics.user.name}</p>
              <p className="text-sm text-gray-600">{statistics.user.phone}</p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Appointments"
          value={statistics?.global?.totalAppointments || 0}
          icon={CalendarIcon}
          color="blue"
        />
        <MetricCard
          title="Show Rate"
          value={`${statistics?.global?.showRate || 0}%`}
          icon={CheckCircleIcon}
          color="green"
          subtitle={`${statistics?.global?.appointmentsKept || 0} kept`}
        />
        <MetricCard
          title="Total Spent"
          value={`₹${(
            purchaseHistory?.global?.totalSpent || 0
          ).toLocaleString()}`}
          icon={ShoppingBagIcon}
          color="purple"
          subtitle={`${purchaseHistory?.global?.totalPurchases || 0} purchases`}
        />
        <MetricCard
          title="Loyalty Points"
          value={loyalty?.loyalty?.points || 0}
          icon={StarIcon}
          color="yellow"
          subtitle={loyalty?.loyalty?.membershipTier || "Regular"}
        />
      </div>

      {/* Reliability Score */}
      {behavior?.reliability && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5 text-green-600" />
            Reliability Score
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl font-bold">
                  {behavior.reliability.score}%
                </span>
                <Badge
                  variant={
                    behavior.reliability.level === "EXCELLENT"
                      ? "success"
                      : behavior.reliability.level === "GOOD"
                      ? "info"
                      : behavior.reliability.level === "FAIR"
                      ? "warning"
                      : "danger"
                  }
                >
                  {behavior.reliability.level}
                </Badge>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    behavior.reliability.score >= 90
                      ? "bg-green-500"
                      : behavior.reliability.score >= 70
                      ? "bg-blue-500"
                      : behavior.reliability.score >= 50
                      ? "bg-yellow-500"
                      : "bg-red-500"
                  }`}
                  style={{ width: `${behavior.reliability.score}%` }}
                ></div>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {behavior.reliability.message}
              </p>
              {behavior.reliability.stats && (
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-gray-600">Kept</p>
                    <p className="font-semibold">
                      {behavior.reliability.stats.appointmentsKept}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-gray-600">No Shows</p>
                    <p className="font-semibold text-red-600">
                      {behavior.reliability.stats.noShows}
                    </p>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <p className="text-gray-600">Cancelled</p>
                    <p className="font-semibold text-yellow-600">
                      {behavior.reliability.stats.cancelled}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Quick Insights */}
      {insights?.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Quick Recommendations</h3>
          <div className="space-y-2">
            {insights.recommendations.slice(0, 3).map((rec, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  rec.priority === "HIGH"
                    ? "bg-red-50 border-red-200"
                    : rec.priority === "MEDIUM"
                    ? "bg-yellow-50 border-yellow-200"
                    : "bg-blue-50 border-blue-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Badge
                    variant={
                      rec.priority === "HIGH"
                        ? "danger"
                        : rec.priority === "MEDIUM"
                        ? "warning"
                        : "info"
                    }
                    size="sm"
                  >
                    {rec.priority}
                  </Badge>
                  <p className="text-sm flex-1">{rec.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Branch Performance */}
      {statistics?.byBranch && statistics.byBranch.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <LocationMarkerIcon className="w-5 h-5 text-blue-600" />
            Top Branches
          </h3>
          <div className="space-y-3">
            {statistics.byBranch.slice(0, 3).map((branch) => (
              <div
                key={branch.branchCode}
                className="flex items-center justify-between p-3 bg-gray-50 rounded"
              >
                <div className="flex-1">
                  <p className="font-medium">{branch.branchName}</p>
                  <p className="text-sm text-gray-600">
                    {branch.totalAppointments} appointments • {branch.showRate}%
                    show rate
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">
                    ₹{(branch.totalSpent || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {branch.conversionRate}% conversion
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Render Appointments Tab
  const renderAppointments = () => (
    <div className="space-y-6">
      {/* Appointment Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Appointments"
          value={statistics?.global?.totalAppointments || 0}
          icon={CalendarIcon}
        />
        <StatCard
          title="Appointments Kept"
          value={statistics?.global?.appointmentsKept || 0}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          title="Cancelled"
          value={statistics?.global?.appointmentsCancelled || 0}
          icon={XCircleIcon}
          color="yellow"
        />
        <StatCard
          title="No Shows"
          value={statistics?.global?.noShows || 0}
          icon={XCircleIcon}
          color="red"
        />
      </div>

      {/* Insights & Recommendations */}
      {insights?.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Recommendations</h3>
          <div className="space-y-3">
            {insights.recommendations.map((rec, index) => (
              <div
                key={index}
                className={`p-4 rounded-lg ${
                  rec.priority === "HIGH"
                    ? "bg-red-50 border border-red-200"
                    : rec.priority === "MEDIUM"
                    ? "bg-yellow-50 border border-yellow-200"
                    : "bg-blue-50 border border-blue-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <Badge
                    variant={
                      rec.priority === "HIGH"
                        ? "danger"
                        : rec.priority === "MEDIUM"
                        ? "warning"
                        : "info"
                    }
                    size="sm"
                  >
                    {rec.priority}
                  </Badge>
                  <div className="flex-1">
                    <p className="font-medium">{rec.message}</p>
                    {rec.occasion && (
                      <p className="text-sm text-gray-600 mt-1">
                        {rec.occasion.type} on{" "}
                        {new Date(rec.occasion.date).toLocaleDateString()}
                        {rec.occasion.daysUntil && (
                          <span className="ml-2 text-orange-600 font-medium">
                            ({rec.occasion.daysUntil} days away)
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upcoming Occasions */}
      {insights?.upcomingOccasions && insights.upcomingOccasions.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Upcoming Occasions</h3>
          <div className="space-y-3">
            {insights.upcomingOccasions.map((occasion, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded"
              >
                <div>
                  <p className="font-medium">{occasion.type}</p>
                  <p className="text-sm text-gray-600">
                    {new Date(occasion.date).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <Badge
                    variant={
                      occasion.urgency === "URGENT"
                        ? "danger"
                        : occasion.urgency === "HIGH"
                        ? "warning"
                        : "info"
                    }
                  >
                    {occasion.daysUntil} days
                  </Badge>
                  {occasion.budget && (
                    <p className="text-xs text-gray-500 mt-1">
                      Budget: ₹{(occasion.budget.min || 0).toLocaleString()} - ₹
                      {(occasion.budget.max || 0).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Appointment Preferences */}
      {statistics?.preferences && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">
            Appointment Preferences
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {statistics.preferences.preferredDays?.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Preferred Days</p>
                <div className="flex flex-wrap gap-2">
                  {statistics.preferences.preferredDays.map((day) => (
                    <Badge key={day} variant="info">
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {statistics.preferences.preferredTimeSlot && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Preferred Time</p>
                <Badge variant="info">
                  {statistics.preferences.preferredTimeSlot}
                </Badge>
              </div>
            )}
            {statistics.preferences.reminderSettings?.enabled && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Reminders</p>
                <Badge variant="success">Enabled</Badge>
                <p className="text-xs text-gray-500 mt-1">
                  {statistics.preferences.reminderSettings.advanceTime} min
                  before
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Best Times to Book */}
      {insights?.bestTimesToBook && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Booking Patterns</h3>
          <div className="grid grid-cols-2 gap-4">
            {insights.bestTimesToBook.preferredDay && (
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600 mb-1">Preferred Day</p>
                <p className="font-semibold capitalize">
                  {insights.bestTimesToBook.preferredDay}
                </p>
              </div>
            )}
            {insights.bestTimesToBook.preferredTimeSlot && (
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600 mb-1">Preferred Time</p>
                <p className="font-semibold">
                  {insights.bestTimesToBook.preferredTimeSlot}
                </p>
              </div>
            )}
          </div>
          {insights.bestTimesToBook.recommendation && (
            <p className="text-sm text-gray-600 mt-3">
              💡 {insights.bestTimesToBook.recommendation}
            </p>
          )}
        </div>
      )}
    </div>
  );

  // Render Purchases Tab
  const renderPurchases = () => (
    <div className="space-y-6">
      {/* Purchase Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Visits"
          value={purchaseHistory?.global?.totalVisits || 0}
          icon={CalendarIcon}
        />
        <StatCard
          title="Total Purchases"
          value={purchaseHistory?.global?.totalPurchases || 0}
          icon={ShoppingBagIcon}
        />
        <StatCard
          title="Total Spent"
          value={`₹${(
            purchaseHistory?.global?.totalSpent || 0
          ).toLocaleString()}`}
          icon={TrendingUpIcon}
          color="green"
        />
        <StatCard
          title="Conversion Rate"
          value={`${purchaseHistory?.global?.conversionRate || 0}%`}
          icon={ChartBarIcon}
          color="blue"
        />
      </div>

      {/* Branch-wise Purchases */}
      {purchaseHistory?.byBranch && purchaseHistory.byBranch.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Branch-wise Purchases</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                    Branch
                  </th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                    Purchases
                  </th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                    Amount
                  </th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                    Avg Ticket
                  </th>
                  <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">
                    Conversion
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {purchaseHistory.byBranch.map((branch) => (
                  <tr key={branch.branchCode}>
                    <td className="px-4 py-3">
                      <p className="font-medium">{branch.branchName}</p>
                      <p className="text-xs text-gray-500">
                        {branch.branchCode}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {branch.totalPurchases}
                    </td>
                    <td className="px-4 py-3 text-right">
                      ₹{(branch.totalSpent || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      ₹{(branch.averageTicketSize || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Badge
                        variant={
                          branch.conversionRate >= 50
                            ? "success"
                            : branch.conversionRate >= 30
                            ? "info"
                            : "warning"
                        }
                      >
                        {branch.conversionRate}%
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Purchases */}
      {purchaseHistory?.recentPurchases &&
        purchaseHistory.recentPurchases.length > 0 && (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Top Products</h3>
            <div className="space-y-2">
              {purchaseHistory.recentPurchases.map((product, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded"
                >
                  <div>
                    <p className="font-medium">{product.productName}</p>
                    <p className="text-xs text-gray-500">
                      Last purchased:{" "}
                      {new Date(product.lastPurchased).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge variant="info">{product.purchaseCount}×</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
    </div>
  );

  // Render Behavior Tab
  const renderBehavior = () => (
    <div className="space-y-6">
      {/* Booking Patterns */}
      {behavior?.bookingPatterns && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Booking Patterns</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">Pattern</p>
              <Badge
                variant={
                  behavior.bookingPatterns.pattern === "FREQUENT"
                    ? "success"
                    : behavior.bookingPatterns.pattern === "REGULAR"
                    ? "info"
                    : "warning"
                }
              >
                {behavior.bookingPatterns.pattern}
              </Badge>
              <p className="text-sm text-gray-600 mt-2">
                {behavior.bookingPatterns.message}
              </p>
            </div>
            {behavior.bookingPatterns.totalAppointments && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Total Appointments</p>
                <p className="text-2xl font-bold">
                  {behavior.bookingPatterns.totalAppointments}
                </p>
              </div>
            )}
            {behavior.bookingPatterns.averageFrequencyDays && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Average Frequency</p>
                <p className="text-2xl font-bold">
                  {behavior.bookingPatterns.averageFrequencyDays} days
                </p>
              </div>
            )}
            {behavior.bookingPatterns.showRate && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Show Rate</p>
                <p className="text-2xl font-bold">
                  {behavior.bookingPatterns.showRate}%
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Conversion Patterns */}
      {behavior?.conversionPatterns && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Conversion Patterns</h3>
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Overall Conversion</p>
            <p className="text-2xl font-bold">
              {behavior.conversionPatterns.overallConversion}%
            </p>
          </div>
          {behavior.conversionPatterns.bestConvertingBranch && (
            <div className="p-4 bg-green-50 rounded border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-2">
                Best Converting Branch
              </p>
              <p className="font-semibold">
                {behavior.conversionPatterns.bestConvertingBranch.branchName}
              </p>
              <p className="text-sm text-gray-600">
                {
                  behavior.conversionPatterns.bestConvertingBranch
                    .conversionRate
                }
                % conversion • ₹
                {behavior.conversionPatterns.bestConvertingBranch.avgTicketSize?.toLocaleString()}{" "}
                avg ticket
              </p>
            </div>
          )}
        </div>
      )}

      {/* Branch Loyalty */}
      {behavior?.branchLoyalty && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Branch Loyalty</h3>
          <div>
            <div className="flex items-center justify-between mb-4">
              <Badge
                variant={
                  behavior.branchLoyalty.level === "HIGHLY_LOYAL"
                    ? "success"
                    : behavior.branchLoyalty.level === "LOYAL"
                    ? "info"
                    : "warning"
                }
              >
                {behavior.branchLoyalty.level.replace("_", " ")}
              </Badge>
              <span className="text-sm text-gray-600">
                {behavior.branchLoyalty.visitedBranches} branches visited
              </span>
            </div>
            {behavior.branchLoyalty.primaryBranch && (
              <div className="p-4 bg-gray-50 rounded">
                <p className="font-medium">
                  {behavior.branchLoyalty.primaryBranch.name}
                </p>
                <p className="text-sm text-gray-600">
                  {behavior.branchLoyalty.primaryBranch.visits} visits (
                  {behavior.branchLoyalty.primaryBranch.percentage}%)
                </p>
              </div>
            )}
            <p className="text-sm text-gray-600 mt-3">
              {behavior.branchLoyalty.message}
            </p>
          </div>
        </div>
      )}

      {/* Seasonal Trends */}
      {behavior?.seasonalTrends &&
        behavior.seasonalTrends.peakMonth &&
        behavior.seasonalTrends.message !==
          "Not enough data for seasonal analysis" && (
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Seasonal Trends</h3>
            <div className="p-4 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm text-gray-600 mb-1">Peak Month</p>
              <p className="text-xl font-bold text-blue-800">
                {behavior.seasonalTrends.peakMonth}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {behavior.seasonalTrends.message}
              </p>
            </div>
          </div>
        )}

      {/* Risk Assessment */}
      {behavior?.riskScore && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">
            No-Show Risk Assessment
          </h3>
          <div>
            <div className="flex items-center justify-between mb-4">
              <Badge
                variant={
                  behavior.riskScore.level === "LOW"
                    ? "success"
                    : behavior.riskScore.level === "MEDIUM"
                    ? "warning"
                    : "danger"
                }
              >
                {behavior.riskScore.level} RISK
              </Badge>
              <span className="text-2xl font-bold">
                {behavior.riskScore.score}%
              </span>
            </div>
            <p className="text-sm text-gray-600 mb-2">
              {behavior.riskScore.message}
            </p>
            <div className="p-3 bg-blue-50 rounded border border-blue-200">
              <p className="text-sm font-medium text-blue-800">
                💡 Recommendation: {behavior.riskScore.recommendation}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lifetime Value */}
      {behavior?.lifetimeValue && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Lifetime Value</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded">
              <p className="text-sm text-gray-600 mb-1">Current Value</p>
              <p className="text-xl font-bold">
                ₹{(behavior.lifetimeValue.currentValue || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-green-50 rounded">
              <p className="text-sm text-gray-600 mb-1">Predicted Value</p>
              <p className="text-xl font-bold text-green-600">
                ₹{(behavior.lifetimeValue.predictedValue || 0).toLocaleString()}
              </p>
            </div>
            <div className="p-4 bg-blue-50 rounded">
              <p className="text-sm text-gray-600 mb-1">Avg Ticket Size</p>
              <p className="text-xl font-bold text-blue-600">
                ₹
                {(
                  behavior.lifetimeValue.averageTicketSize || 0
                ).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render Loyalty Tab
  const renderLoyalty = () => (
    <div className="space-y-6">
      {/* Loyalty Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Current Points"
          value={loyalty?.loyalty?.points || 0}
          icon={StarIcon}
          color="yellow"
        />
        <StatCard
          title="Points Earned"
          value={loyalty?.loyalty?.pointsEarned || 0}
          icon={TrendingUpIcon}
          color="green"
        />
        <StatCard
          title="Points Redeemed"
          value={loyalty?.loyalty?.pointsRedeemed || 0}
          icon={ShoppingBagIcon}
          color="purple"
        />
      </div>

      {/* Membership Tier */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Membership Status</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-2">Current Tier</p>
            <Badge
              variant={
                loyalty?.loyalty?.membershipTier === "VIP" ||
                loyalty?.loyalty?.membershipTier === "PLATINUM"
                  ? "success"
                  : loyalty?.loyalty?.membershipTier === "GOLD"
                  ? "warning"
                  : "info"
              }
              size="lg"
            >
              {loyalty?.loyalty?.membershipTier || "REGULAR"}
            </Badge>
            {loyalty?.loyalty?.tierUpgradeDate && (
              <p className="text-xs text-gray-500 mt-2">
                Upgraded on{" "}
                {new Date(loyalty.loyalty.tierUpgradeDate).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Referrals */}
      {loyalty?.loyalty?.referrals && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Referral Program</h3>
          <div className="space-y-3">
            {loyalty.loyalty.referralCode && (
              <div>
                <p className="text-sm text-gray-600 mb-1">Referral Code</p>
                <p className="font-mono font-bold text-lg">
                  {loyalty.loyalty.referralCode}
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-gray-50 rounded">
                <p className="text-sm text-gray-600">Total Referrals</p>
                <p className="text-xl font-bold">
                  {loyalty.loyalty.referrals.totalReferrals || 0}
                </p>
              </div>
              <div className="p-3 bg-green-50 rounded">
                <p className="text-sm text-gray-600">Successful</p>
                <p className="text-xl font-bold text-green-600">
                  {loyalty.loyalty.referrals.successfulReferrals || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loyalty History (if available) */}
      {loyalty?.loyalty?.history && loyalty.loyalty.history.length > 0 && (
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
          <div className="space-y-2">
            {loyalty.loyalty.history.slice(0, 5).map((transaction, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded"
              >
                <div>
                  <Badge
                    variant={
                      transaction.type === "EARNED" ? "success" : "warning"
                    }
                    size="sm"
                  >
                    {transaction.type}
                  </Badge>
                  <p className="text-sm text-gray-600 mt-1">
                    {transaction.reason}
                  </p>
                  <p className="text-xs text-gray-500">
                    {new Date(transaction.date).toLocaleDateString()}
                  </p>
                </div>
                <p
                  className={`text-lg font-bold ${
                    transaction.type === "EARNED"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {transaction.type === "EARNED" ? "+" : "-"}
                  {transaction.points}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={
        <div className="flex items-center gap-3">
          <ChartBarIcon className="w-6 h-6 text-primary" />
          <span>Customer Analytics - {userName}</span>
        </div>
      }
      isFullScreen={true}
    >
      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-primary text-primary bg-primary-light/10"
                  : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-h-[600px] overflow-y-auto">
        {activeTab === "overview" && renderOverview()}
        {activeTab === "appointments" && renderAppointments()}
        {activeTab === "purchases" && renderPurchases()}
        {activeTab === "behavior" && renderBehavior()}
        {activeTab === "loyalty" && renderLoyalty()}
      </div>
    </Modal>
  );
};

// Helper Components
const MetricCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200">
    <div className="flex items-center justify-between mb-2">
      <p className="text-sm text-gray-600">{title}</p>
      <Icon
        className={`w-5 h-5 ${
          color === "blue"
            ? "text-blue-600"
            : color === "green"
            ? "text-green-600"
            : color === "purple"
            ? "text-purple-600"
            : color === "yellow"
            ? "text-yellow-600"
            : "text-gray-600"
        }`}
      />
    </div>
    <p className="text-2xl font-bold">{value}</p>
    {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
  </div>
);

const StatCard = ({ title, value, icon: Icon, color }) => (
  <div className="bg-white p-4 rounded-lg border border-gray-200">
    <div className="flex items-center gap-3">
      <div
        className={`p-3 rounded-lg ${
          color === "green"
            ? "bg-green-100"
            : color === "red"
            ? "bg-red-100"
            : color === "yellow"
            ? "bg-yellow-100"
            : "bg-blue-100"
        }`}
      >
        <Icon
          className={`w-6 h-6 ${
            color === "green"
              ? "text-green-600"
              : color === "red"
              ? "text-red-600"
              : color === "yellow"
              ? "text-yellow-600"
              : "text-blue-600"
          }`}
        />
      </div>
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-xl font-bold">{value}</p>
      </div>
    </div>
  </div>
);

export default UserAnalyticsDashboard;
